import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessagePreviewPanel } from './MessagePreviewPanel';
import { VariableInserter } from '@/components/ui/VariableInserter';

interface Employee {
  employee_id: string;
  name: string;
  email: string;
  phone: string;
  timezone: string;
  avatar_url?: string;
  slack_id?: string;
  display_name?: string;
  phone_number?: string;
}

interface CheckIn {
  check_in_id: string;
  name: string;
  description: string | null;
  slug: string;
  template_name: string;
  template_id: string;
  cadence: 'daily' | 'weekly' | 'monthly' | 'one-off' | 'on-demand' | 'bi-weekly';
  days_of_week: string[] | null;
  scheduled_day: string | null;
  reminder_time: string;
  cutoff_time: string;
  timezone: string;
  timezone_reminder: boolean;
  next_report_date_time: string | null;
  created_at: string;
  communication_channel: 'slack' | 'email';
  slack_workspace_id: string | null;
  slack_notification_enabled: boolean;
  report_sends: 'email' | 'slack_channel' | 'slack_dm' | 'none';
  report_slack_channel_id: string | null;
  report_slack_dm_user_id: string | null;
  report_email_addresses: string[] | null;
  submission_alerts_enabled: boolean;
  is_active: boolean;
  min_reminder_offset_hours: number;
  one_off_due_date: string | null;
  active_question_ids?: string[];
  reminder_message_template: string;
  reminder_email_subject_template: string | null;
}

interface MessageCustomizationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (messageTemplate: string, subjectTemplate: string | null) => void;
  checkIn: CheckIn;
  participants: Employee[];
  organizationName: string;
  senderName: string;
  form: any;
  selectedPreviewParticipant: Employee | null;
  handlePreviewParticipantChange: (participantId: string) => void;
  handleVariableInsert: (variable: string, fieldRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>) => void;
  resetToDefaults: () => void;
}

export function MessageCustomizationDialog({
  isOpen,
  onClose,
  onSave,
  checkIn,
  participants,
  organizationName,
  senderName,
  form,
  selectedPreviewParticipant,
  handlePreviewParticipantChange,
  handleVariableInsert,
  resetToDefaults
}: MessageCustomizationDialogProps) {
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    const messageTemplate = form.getValues('reminder_message_template');
    const subjectTemplate = form.getValues('reminder_email_subject_template');
    onSave(messageTemplate, subjectTemplate);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customize Reminder Messages</DialogTitle>
          <DialogDescription>
            Personalize the reminder messages sent to check-in participants using template variables.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Message Customization */}
            <div className="space-y-6">
              {/* Email Subject (only for email check-ins) */}
              {checkIn.communication_channel === 'email' && (
                <FormField
                  control={form.control}
                  name="reminder_email_subject_template"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Email Subject</FormLabel>
                        <VariableInserter
                          onInsert={(variable: string) => handleVariableInsert(variable, subjectInputRef)}
                        />
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          ref={subjectInputRef}
                          placeholder="Enter email subject template..."
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormDescription>
                        The subject line for reminder emails. Use variables to personalize the message.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Message Template */}
              <FormField
                control={form.control}
                name="reminder_message_template"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>
                        {checkIn.communication_channel === 'email' ? 'Email Message' : 'Slack Message'}
                      </FormLabel>
                      <VariableInserter
                        onInsert={(variable: string) => handleVariableInsert(variable, messageTextareaRef)}
                      />
                    </div>
                    <FormControl>
                      <Textarea
                        {...field}
                        ref={messageTextareaRef}
                        placeholder={`Enter ${checkIn.communication_channel} message template...`}
                        rows={8}
                        className="resize-none"
                      />
                    </FormControl>
                    <FormDescription>
                      The main reminder message sent to participants. Use variables to personalize the content.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Reset Button */}
              <div className="flex justify-start">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetToDefaults}
                  size="sm"
                >
                  Reset to Defaults
                </Button>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className="space-y-4">
              {/* Participant Selection for Preview */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Preview for participant:</label>
                <Select 
                  value={selectedPreviewParticipant?.employee_id || ''} 
                  onValueChange={handlePreviewParticipantChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a participant to preview" />
                  </SelectTrigger>
                  <SelectContent>
                    {participants.map((participant) => (
                      <SelectItem key={participant.employee_id} value={participant.employee_id}>
                        {participant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message Preview */}
              <MessagePreviewPanel
                checkIn={checkIn}
                organizationName={organizationName}
                senderName={senderName}
                selectedParticipant={selectedPreviewParticipant}
                messageTemplate={form.watch('reminder_message_template')}
                subjectTemplate={form.watch('reminder_email_subject_template')}
                isEmail={checkIn.communication_channel === 'email'}
              />
            </div>
          </div>
        </Form>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Message Templates
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 